import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { COLORS, DSP_NAMES_FORMATTED } from 'config/constants';
import ENTITY_PROPTYPES from 'config/constants/propTypes';
import { arrayToCommaList } from 'helpers/text';
import { generateSelectorsFromTestIds } from 'helpers/utilities';
import useBreakpoint from 'hooks/useBreakpoint';

import ActionMenu, {
  ActionMenuHeader,
  ActionMenuList,
  ArtistHeader
} from 'components/ActionMenu';
import { EllipsisButton } from 'components/Button';
import EntityElement from 'components/EntityElement';
import { DropdownMenuItem, StyledDropdownMenu } from 'components/DropdownMenu';
import Flex from 'components/Flex';
import { DspIcon, PersonIcon } from 'components/Icons';
import MenuToggler from 'components/MenuToggler';
import CommaList from 'components/CommaList';

const StyledDspIcon = styled(DspIcon)`
  color: ${COLORS.GRAY_LIGHT};
  margin-right: 0.4rem;
`;

const DspLink = styled.a`
  svg {
    &:hover {
      color: ${COLORS.PRIMARY};
    }
  }
`;

/**
 * [ArtistSummaryRow] - artist row that displays artist name, connections, and members based on supplied artist data
 *
 * @param {object} artist - can supply from algolia or artist profile.  NOTE: if passing from algolia, only countries
 * will be displayed instead of members AND countries
 * @param {bool} [enableLinks] - by default, links are not enabled and instead can be accessed from the popover or
 * action menu
 * @param {node | func} [children] - render additional content to the left of the action dropdown
 */

const ENTITY_PROPS = {
  size: EntityElement.SIZES.SMALL,
  type: EntityElement.TYPES.ARTIST
};

const NO_CONNECTIONS = [];

const ArtistSummaryRow = ({
  artist = {},
  className,
  children,
  enableLinks,
  lazyLoadImage = false,
  'data-testid': testId
}) => {
  const connections = artist.connections || NO_CONNECTIONS;
  // if artist index (algolia), only countries will be displayed
  const memberCountryArray = artist.members
    ? artist.members.map(({ first_name, last_name, country }) => {
        const countryFormatted = country ? ` (${country})` : '';
        const nameFormatted = `${first_name ? `${first_name} ` : ''}${last_name || ''}`;

        if (!first_name && !last_name) {
          return `Unknown ${countryFormatted}`;
        }
        return nameFormatted + countryFormatted;
      })
    : artist.countries || [];

  const isSmallScreen = useBreakpoint(
    useBreakpoint.BREAKPOINTS.SMALL,
    useBreakpoint.DIRECTIONS.DOWN
  );

  const actions = useMemo(() => {
    const iconProps = { size: 20, color: 'currentColor' };

    return [
      ...(artist.slug
        ? [
            {
              name: 'View Profile',
              to: artist.slug,
              icon: <PersonIcon {...iconProps} />,
              showIcon: isSmallScreen
            }
          ]
        : []),
      ...connections.map(({ dsp, dsp_url }) => ({
        name: `View ${DSP_NAMES_FORMATTED[dsp]} profile`,
        newTab: true,
        to: dsp_url,
        icon: <DspIcon service={dsp} {...iconProps} />,
        showIcon: isSmallScreen
      }))
    ];
  }, [isSmallScreen, connections, artist]);

  // don't bother applying a title attribute unless enough content to get clipped
  const memberCountryLimit = artist.members ? 2 : 5;

  const subtitle = (
    <>
      <span
        title={
          memberCountryArray.length > memberCountryLimit
            ? arrayToCommaList(memberCountryArray)
            : undefined
        }
      >
        <CommaList data={memberCountryArray} />
      </span>
    </>
  );

  return (
    <Flex data-testid={testId || ArtistSummaryRow.TEST_IDS.CONTAINER}>
      <EntityElement
        {...ENTITY_PROPS}
        imageUrl={artist.image_url}
        lazy={lazyLoadImage}
        enableLinks={!!enableLinks}
        data={artist}
        title={
          <Flex>
            {artist.slug && enableLinks ? (
              <Link to={artist.slug} target="_blank" rel="noopener noreferrer">
                {artist.name}
              </Link>
            ) : (
              <span>{artist.name}</span>
            )}

            <Flex className="ml-1" align="center">
              {connections.map(({ id, dsp, dsp_url }) =>
                enableLinks ? (
                  <DspLink
                    href={dsp_url}
                    key={id}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <StyledDspIcon service={dsp} color="currentColor" size={14} />
                  </DspLink>
                ) : (
                  <StyledDspIcon key={id} service={dsp} color="currentColor" size={14} />
                )
              )}
            </Flex>
          </Flex>
        }
        subtitle={subtitle}
        className={className}
      >
        {children}

        {actions.length > 0 && (
          <MenuToggler
            renderToggle={({ toggle, actionMenu }) => {
              const ToggleButton = actionMenu ? EllipsisButton.Mobile : EllipsisButton;

              return (
                <ToggleButton
                  onClick={e => {
                    e.stopPropagation();
                    toggle(e);
                  }}
                />
              );
            }}
            renderActionMenu={({ isOpen, close }) => (
              <ActionMenu close={close} isOpen={isOpen}>
                <ActionMenuHeader>
                  <ArtistHeader
                    image_url={artist.image_url}
                    name={artist.name}
                    subtitle={subtitle}
                  />
                </ActionMenuHeader>

                <ActionMenuList actions={actions} closeMenu={close} />
              </ActionMenu>
            )}
            renderPopoverContent={() => (
              <StyledDropdownMenu>
                {actions.map(action => (
                  <DropdownMenuItem key={action.name} {...action}>
                    {action.name}
                  </DropdownMenuItem>
                ))}
              </StyledDropdownMenu>
            )}
          />
        )}
      </EntityElement>
    </Flex>
  );
};

ArtistSummaryRow.propTypes = {
  artist: PropTypes.oneOfType([ENTITY_PROPTYPES.ARTIST_PROFILE, ENTITY_PROPTYPES.ARTIST]),
  children: PropTypes.node,
  className: PropTypes.string,
  enableLinks: PropTypes.bool,
  lazyLoadImage: PropTypes.bool,
  'data-testid': PropTypes.string
};

ArtistSummaryRow.Tombstone = ({ index, className }) => (
  <EntityElement.Tombstone {...ENTITY_PROPS} className={className} index={index} />
);

ArtistSummaryRow.TEST_IDS = {
  CONTAINER: 'ArtistSummaryRow-container'
};

ArtistSummaryRow.SELECTORS = generateSelectorsFromTestIds(ArtistSummaryRow.TEST_IDS);

export default ArtistSummaryRow;
